import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const googleMapKey = process.env.REACT_APP_GOOGLE_MAP_KEY;

// Google Maps libraries
const libraries = ["places"];

/**
 * LocationViewDialog Component
 * 
 * Displays a location on a map in a view-only dialog
 * Used when clicking on location messages in chat
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Object} props.locationData - Location data object with:
 *   - latLong: { lat: number, long: number }
 *   - location: string (address)
 */
const LocationViewDialog = ({ open, onClose, locationData }) => {
  const [currentLocation, setCurrentLocation] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: googleMapKey,
    libraries: libraries,
  });

  // set location from props when dialog opens
  useEffect(() => {
    if (open && locationData?.latLong) {
      setCurrentLocation({
        lat: locationData.latLong.lat,
        lng: locationData.latLong.long,
      });
    }
  }, [open, locationData]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, textTransform: "lowercase" }}>
          location
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            padding: 0.5,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {loadError ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
            <Typography color="error">error loading map</Typography>
          </Box>
        ) : !isLoaded || !currentLocation ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* map display */}
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={currentLocation}
              zoom={15}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: true,
                mapTypeControl: false,
              }}
            >
              <Marker
                position={currentLocation}
                title={locationData?.location || "location"}
              />
            </GoogleMap>

            {/* address display */}
            {locationData?.location && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, textTransform: "lowercase" }}>
                  address:
                </Typography>
                <Typography variant="body1" sx={{ textTransform: "lowercase" }}>
                  {locationData.location}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LocationViewDialog;

